import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Snackbar,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  CreditCard as CreditCardIcon,
  TrendingUp as TrendingUpIcon,
  Pause as PauseIcon,
  PlayArrow as ResumeIcon,
  SwapHoriz as ChangePlanIcon,
} from '@mui/icons-material';
import API_ENDPOINTS from '../config/api';

interface Plan {
  id: string;
  name: string;
  description: string;
  amount: number;
  currency: string;
  recurring: boolean;
  interval?: string;
  tokens_included?: number;
  product_id?: string;  // Dodo Payments product ID
}

interface Subscription {
  id: string;
  plan_type: string;
  status: string;
  tokens_per_month: number;
  amount: number;
  currency: string;
  current_period_start: string;
  current_period_end: string;
  dodo_subscription_id?: string;
  checkout_url?: string;  // URL to complete payment/subscription
  payment_link?: string;  // Direct payment link from Dodo
  dodo_status?: any;  // Real-time status from Dodo Payments
}

interface Payment {
  id: string;
  amount: number;
  currency: string;
  status: string;
  payment_method?: string;
  tokens_added: number;
  created_at: string;
  dodo_payment_id: string;
}

const SubscriptionManager: React.FC = () => {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [showSubscribeDialog, setShowSubscribeDialog] = useState(false);
  const [subscribing, setSubscribing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'plans' | 'subscriptions' | 'payments'>('plans');
  const [notification, setNotification] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' | 'warning' }>({
    open: false,
    message: '',
    severity: 'info'
  });

  const token = localStorage.getItem('gamora_token');

  const showNotification = (message: string, severity: 'success' | 'error' | 'info' | 'warning' = 'info') => {
    setNotification({ open: true, message, severity });
  };

  const handleCloseNotification = () => {
    setNotification(prev => ({ ...prev, open: false }));
  };

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Load plans first (no auth required)
      const plansRes = await fetch(API_ENDPOINTS.PLANS);
      console.log('Plans response status:', plansRes.status);
      console.log('Plans response headers:', plansRes.headers);
      
      if (plansRes.ok) {
        const plansData = await plansRes.json();
        console.log('Loaded plans:', plansData);
        setPlans(plansData);
        setError(null); // Clear any previous errors
      } else {
        console.error('Failed to load plans:', plansRes.status, plansRes.statusText);
        const errorText = await plansRes.text();
        console.error('Error response body:', errorText);
        setError(`Failed to load subscription plans: ${plansRes.status} ${plansRes.statusText}`);
      }
      
      // Load user-specific data only if authenticated
      if (token) {
        const [subscriptionsRes, paymentsRes] = await Promise.all([
          fetch(API_ENDPOINTS.MY_SUBSCRIPTIONS, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          fetch(API_ENDPOINTS.MY_PAYMENTS, {
            headers: { Authorization: `Bearer ${token}` }
          })
        ]);

        if (subscriptionsRes.ok) {
          const subscriptionsData = await subscriptionsRes.json();
          console.log('Loaded subscriptions:', subscriptionsData);
          console.log('Number of subscriptions:', subscriptionsData.length);
          setSubscriptions(subscriptionsData);
        } else {
          console.error('Failed to load subscriptions:', subscriptionsRes.status, subscriptionsRes.statusText);
          const errorText = await subscriptionsRes.text();
          console.error('Subscriptions error response:', errorText);
        }

        if (paymentsRes.ok) {
          const paymentsData = await paymentsRes.json();
          setPayments(paymentsData);
        }
      }
    } catch (error) {
      console.error('Error loading data:', error);
      // Don't set error here since we handle it per request above
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSubscribe = async () => {
    if (!selectedPlan) return;

    try {
      setSubscribing(true);
      setError(null);

      const response = await fetch(`${API_ENDPOINTS.SUBSCRIBE}?plan_id=${selectedPlan.id}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const result = await response.json();
        setShowSubscribeDialog(false);
        setSelectedPlan(null);
        
        // Check if we have a payment link or checkout URL
        const paymentUrl = result.payment_link || result.checkout_url;
        if (paymentUrl) {
          // Redirect user to Dodo to complete payment
          window.open(paymentUrl, '_blank');
          showNotification(`Redirecting to payment page for ${selectedPlan.name}. Please complete your payment to activate your subscription.`, 'info');
        } else {
          // No payment URL, show error
          showNotification(`Error: No payment link received. Please try again.`, 'error');
          console.error('No payment URL in response:', result);
        }
        
        // Reload data to show new subscription
        await loadData();
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to subscribe');
      }
    } catch (error) {
      setError('Error processing subscription');
      console.error('Subscription error:', error);
    } finally {
      setSubscribing(false);
    }
  };

  const handleCancelSubscription = async (subscriptionId: string) => {
    console.log('Attempting to cancel subscription:', subscriptionId);
    if (!window.confirm('Are you sure you want to cancel this subscription?')) return;

    try {
      const url = API_ENDPOINTS.CANCEL_SUBSCRIPTION(subscriptionId);
      console.log('Cancel URL:', url);
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      console.log('Cancel response status:', response.status);
      
      if (response.ok) {
        await loadData();
        showNotification('Subscription cancelled successfully', 'success');
      } else {
        const errorData = await response.json();
        console.error('Cancel error response:', errorData);
        showNotification(`Failed to cancel subscription: ${errorData.detail}`, 'error');
      }
    } catch (error) {
      showNotification('Error cancelling subscription', 'error');
      console.error('Cancel error:', error);
    }
  };

  const handlePauseSubscription = async (subscriptionId: string) => {
    console.log('Attempting to pause subscription:', subscriptionId);
    if (!window.confirm('Are you sure you want to pause this subscription?')) return;

    try {
      const url = API_ENDPOINTS.PAUSE_SUBSCRIPTION(subscriptionId);
      console.log('Pause URL:', url);
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      console.log('Pause response status:', response.status);
      
      if (response.ok) {
        await loadData();
        showNotification('Subscription paused successfully', 'success');
      } else {
        const errorData = await response.json();
        console.error('Pause error response:', errorData);
        showNotification(`Failed to pause subscription: ${errorData.detail}`, 'error');
      }
    } catch (error) {
      showNotification('Error pausing subscription', 'error');
      console.error('Pause error:', error);
    }
  };

  const handleResumeSubscription = async (subscriptionId: string) => {
    try {
      const response = await fetch(API_ENDPOINTS.RESUME_SUBSCRIPTION(subscriptionId), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        await loadData();
        showNotification('Subscription resumed successfully', 'success');
      } else {
        const errorData = await response.json();
        showNotification(`Failed to resume subscription: ${errorData.detail}`, 'error');
      }
    } catch (error) {
      showNotification('Error resuming subscription', 'error');
      console.error('Resume error:', error);
    }
  };

  const handleChangePlan = async (subscriptionId: string, newPlanId: string) => {
    if (!window.confirm('Are you sure you want to change to this plan?')) return;

    try {
      const response = await fetch(API_ENDPOINTS.CHANGE_PLAN(subscriptionId, newPlanId), {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        await loadData();
        showNotification('Subscription plan changed successfully', 'success');
      } else {
        const errorData = await response.json();
        showNotification(`Failed to change plan: ${errorData.detail}`, 'error');
      }
    } catch (error) {
      showNotification('Error changing plan', 'error');
      console.error('Change plan error:', error);
    }
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount / 100);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, mb: 4 }}>
        Subscription Management
      </Typography>

      {/* Tab Navigation */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 4 }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant={activeTab === 'plans' ? 'contained' : 'text'}
            onClick={() => setActiveTab('plans')}
            startIcon={<TrendingUpIcon />}
            sx={{ 
              borderRadius: 2,
              px: 3,
              py: 1.5,
              fontWeight: activeTab === 'plans' ? 600 : 500
            }}
          >
            Available Plans
          </Button>
          <Button
            variant={activeTab === 'subscriptions' ? 'contained' : 'text'}
            onClick={() => setActiveTab('subscriptions')}
            startIcon={<CheckIcon />}
            sx={{ 
              borderRadius: 2,
              px: 3,
              py: 1.5,
              fontWeight: activeTab === 'subscriptions' ? 600 : 500
            }}
          >
            My Subscriptions ({subscriptions.length})
          </Button>
          <Button
            variant={activeTab === 'payments' ? 'contained' : 'text'}
            onClick={() => setActiveTab('payments')}
            startIcon={<CreditCardIcon />}
            sx={{ 
              borderRadius: 2,
              px: 3,
              py: 1.5,
              fontWeight: activeTab === 'payments' ? 600 : 500
            }}
          >
            Payment History ({payments.length})
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Available Plans Tab */}
      {activeTab === 'plans' && (
        <Card sx={{ mb: 4, borderRadius: 3, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)' }}>
          <CardContent sx={{ p: 4 }}>
                      <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
            Available Plans
          </Typography>
          
          {plans.length > 0 ? (
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, 
              gap: 3 
            }}>
              {plans.map((plan) => (
                <Card 
                  key={plan.id}
                  sx={{ 
                    height: '100%',
                    border: '2px solid #e2e8f0',
                    borderRadius: 3,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      borderColor: '#3b82f6',
                      transform: 'translateY(-4px)',
                      boxShadow: '0 12px 32px rgba(59, 130, 246, 0.15)'
                    }
                  }}
                >
                  <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                        {plan.name}
                      </Typography>
                                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {plan.description}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <Typography variant="h4" sx={{ fontWeight: 800, color: '#3b82f6' }}>
                        {formatCurrency(plan.amount, plan.currency)}
                      </Typography>
                      {plan.recurring && (
                        <Typography variant="body2" color="text.secondary">
                          /{plan.interval}
                        </Typography>
                      )}
                    </Box>

                      {plan.tokens_included && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                          <TrendingUpIcon color="primary" />
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {plan.tokens_included.toLocaleString()} tokens
                          </Typography>
                        </Box>
                      )}
                    </Box>

                    <Box sx={{ mt: 'auto' }}>
                      <Button
                        variant="contained"
                        fullWidth
                        size="large"
                        sx={{
                          background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                          borderRadius: 2,
                          py: 1.5,
                          textTransform: 'none',
                          fontWeight: 600,
                          fontSize: '1rem'
                        }}
                        onClick={() => {
                          setSelectedPlan(plan);
                          setShowSubscribeDialog(true);
                        }}
                      >
                        {plan.recurring ? 'Subscribe' : 'Purchase'}
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <TrendingUpIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No Plans Available
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Subscription plans are currently being configured. Please check back later.
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
      )}

      {/* Current Subscriptions Tab */}
      {activeTab === 'subscriptions' && (
        <Card sx={{ mb: 4, borderRadius: 3, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)' }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
              Current Subscriptions
            </Typography>
            
            {subscriptions.length > 0 ? (
              <List>
                {subscriptions.map((subscription) => (
                  <React.Fragment key={subscription.id}>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemIcon>
                        <CheckIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                              {subscription.plan_type.charAt(0).toUpperCase() + subscription.plan_type.slice(1)} Plan
                            </Typography>
                            <Chip 
                              label={subscription.status} 
                              color={
                                subscription.status === 'ACTIVE' ? 'success' : 
                                subscription.status === 'PENDING' ? 'warning' : 
                                subscription.status === 'PAUSED' ? 'warning' : 
                                subscription.status === 'CANCELLED' ? 'error' : 'default'
                              }
                              size="small"
                            />
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              {formatCurrency(subscription.amount, subscription.currency)} per month
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {subscription.tokens_per_month.toLocaleString()} tokens per month
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Current period: {formatDate(subscription.current_period_start)} - {formatDate(subscription.current_period_end)}
                            </Typography>
                          </Box>
                        }
                      />
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {subscription.status === 'PENDING' && (
                          <Typography variant="body2" color="warning.main" sx={{ fontStyle: 'italic' }}>
                            Payment pending - Please complete your payment to activate this subscription
                          </Typography>
                        )}
                        {subscription.status === 'CANCELLED' && (
                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            This subscription has been cancelled
                          </Typography>
                        )}
                        {subscription.status === 'ACTIVE' && (
                          <>
                            <Button
                              variant="outlined"
                              color="warning"
                              startIcon={<PauseIcon />}
                              onClick={() => handlePauseSubscription(subscription.id)}
                              size="small"
                            >
                              Pause
                            </Button>
                            <Button
                              variant="outlined"
                              color="primary"
                              startIcon={<ChangePlanIcon />}
                              onClick={() => {
                                const newPlanId = prompt('Enter new plan ID to change to:');
                                if (newPlanId) handleChangePlan(subscription.id, newPlanId);
                              }}
                              size="small"
                            >
                              Change Plan
                            </Button>
                            <Button
                              variant="outlined"
                              color="error"
                              startIcon={<CancelIcon />}
                              onClick={() => handleCancelSubscription(subscription.id)}
                              size="small"
                            >
                              Cancel
                            </Button>
                          </>
                        )}
                        {subscription.status === 'PAUSED' && (
                          <>
                            <Button
                              variant="outlined"
                              color="success"
                              startIcon={<ResumeIcon />}
                              onClick={() => handleResumeSubscription(subscription.id)}
                              size="small"
                            >
                              Resume
                            </Button>
                            <Button
                              variant="outlined"
                              color="error"
                              startIcon={<CancelIcon />}
                              onClick={() => handleCancelSubscription(subscription.id)}
                              size="small"
                            >
                              Cancel
                            </Button>
                          </>
                        )}
                      </Box>
                    </ListItem>
                    <Divider />
                  </React.Fragment>
                ))}
              </List>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <CheckIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No Active Subscriptions
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  You don't have any active subscriptions yet. Choose a plan from the Available Plans tab to get started.
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* Payment History Tab */}
      {activeTab === 'payments' && (
        <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)' }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
              Payment History
            </Typography>
            
            {payments.length > 0 ? (
              <List>
                {payments.map((payment) => (
                  <React.Fragment key={payment.id}>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemIcon>
                        <CreditCardIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                              {formatCurrency(payment.amount, payment.currency)}
                            </Typography>
                            <Chip 
                              label={payment.status} 
                              color={payment.status === 'COMPLETED' ? 'success' : 'default'}
                              size="small"
                            />
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              {payment.tokens_added.toLocaleString()} tokens added
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Date: {formatDate(payment.created_at)}
                            </Typography>
                            {payment.payment_method && (
                              <Typography variant="body2" color="text.secondary">
                                Method: {payment.payment_method}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                    <Divider />
                  </React.Fragment>
                ))}
              </List>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <CreditCardIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No Payment History
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  You haven't made any payments yet. Your payment history will appear here after you subscribe to a plan.
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* Subscribe Dialog */}
      <Dialog 
        open={showSubscribeDialog} 
        onClose={() => setShowSubscribeDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Subscribe to {selectedPlan?.name}
        </DialogTitle>
        <DialogContent>
          {selectedPlan && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body1" gutterBottom>
                You're about to subscribe to the <strong>{selectedPlan.name}</strong> plan.
              </Typography>
              
              <Box sx={{ mt: 3, p: 3, bgcolor: '#f8fafc', borderRadius: 2 }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  Plan Details
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  • {selectedPlan.description}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  • Price: {formatCurrency(selectedPlan.amount, selectedPlan.currency)}
                  {selectedPlan.recurring && ` per ${selectedPlan.interval}`}
                </Typography>
                {selectedPlan.tokens_included && (
                  <Typography variant="body2" color="text.secondary">
                    • Tokens: {selectedPlan.tokens_included.toLocaleString()} per month
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setShowSubscribeDialog(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubscribe}
            disabled={subscribing}
            startIcon={subscribing ? <CircularProgress size={20} /> : null}
          >
            {subscribing ? 'Processing...' : 'Confirm Subscription'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseNotification} 
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SubscriptionManager;
